/**
 * Golden Ball placement for a tournament competition (specs/champions-league AC-4.4/4.5,
 * Story 12).
 *
 * Optimistic: the boost moves the instant it is tapped and reverts if the server refuses.
 * The server owns the rules — which stages are eligible, which round slot a match occupies,
 * and whether that slot is already locked — so a rejection is authoritative, not a retry
 * candidate.
 *
 * One boost per (stage, round) slot. The server's upsert would happily MOVE a boost to a
 * second match in the same slot, but the card does not offer that: once a slot is taken, the
 * other matches in it go dim (`isSlotTakenByOtherMatch`) and the only way to move it is to
 * clear the match that holds it. That is the World Cup's affordance
 * (`useWorldCupPicksData.isBoostLockedByOtherMatch`), and its absence here is what let a player
 * tap x2 on all eight games of a matchday with nothing on screen saying only one would count.
 *
 * Takes the whole match list, not one match at a time, because "is this round's boost already
 * spent?" cannot be answered from a match alone: `hasGoldenBall` is false on every sibling of
 * the match that holds it.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { worldCupQueryKeys } from '../../worldcup/constants/worldcupQueryKeys';
import { useSnackbarStore } from '../../shared/stores/snackbarStore';
import { COMPETITION_PICK_FEEDBACK_COPY } from '../constants/competitionPickFeedback.constants';
import type { ApiMatch } from '../../worldcup/helpers/worldcupPicksData.helper';
import { resolveGoldenBallSlot, slotKey } from '../helpers/tournamentGoldenBall.helper';
import type { CompetitionRulesSummary } from '../types/competitions.types';

interface UseTournamentGoldenBallOpts {
  competitionId: string;
  rules: CompetitionRulesSummary | null | undefined;
  /** Every match the competition has. Empty until the query lands, which is harmless. */
  matches: readonly ApiMatch[] | undefined;
}

/** Slot key → the competitionMatchId currently holding that slot's boost, or null for free. */
type BoostBySlot = Record<string, string | null>;

export function useTournamentGoldenBall({
  competitionId,
  rules,
  matches,
}: UseTournamentGoldenBallOpts) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [localBoosts, setLocalBoosts] = useState<BoostBySlot>({});

  /** Where the server currently has each slot's boost, read off the whole list. */
  const serverBoosts = useMemo<BoostBySlot>(() => {
    const map: BoostBySlot = {};
    for (const match of matches ?? []) {
      if (!match.hasGoldenBall) continue;
      const slot = resolveGoldenBallSlot(match, rules);
      if (!slot.eligible || slot.round == null) continue;
      map[slotKey(match.stage, slot.round)] = match.competitionMatchId;
    }
    return map;
  }, [matches, rules]);

  /**
   * Which match holds a slot right now, preferring the optimistic value over the server's
   * until the refetch lands. Null when the slot is free.
   *
   * The shadowing is deliberate but STRICTLY temporary — see `toggleBoost`, which drops the
   * entry once the refetch it triggered has landed. An entry that outlived that would make the
   * optimistic guess permanent for the life of the hook.
   */
  const holderOfSlot = useCallback(
    (key: string): string | null => {
      if (key in localBoosts) return localBoosts[key];
      return serverBoosts[key] ?? null;
    },
    [localBoosts, serverBoosts],
  );

  /** The slot this match's boost would occupy, or null when it cannot take one. */
  const keyForMatch = useCallback(
    (match: ApiMatch): string | null => {
      const slot = resolveGoldenBallSlot(match, rules);
      if (!slot.eligible || slot.round == null) return null;
      return slotKey(match.stage, slot.round);
    },
    [rules],
  );

  const toggleBoost = useCallback(
    async (match: ApiMatch) => {
      const slot = resolveGoldenBallSlot(match, rules);
      if (!slot.eligible || !slot.placeable || slot.round == null) return;

      const key = slotKey(match.stage, slot.round);
      const current = holderOfSlot(key);
      // Tapping the match that already holds the slot clears it. Tapping any other match in a
      // held slot cannot happen: its control is disabled (AC-12.1).
      const next = current === match.competitionMatchId ? null : match.competitionMatchId;

      setLocalBoosts((prev) => ({ ...prev, [key]: next }));

      /** Hand the slot back to `serverBoosts`. Used by both outcomes, for different reasons. */
      const releaseSlot = () => setLocalBoosts((prev) => {
        if (!(key in prev)) return prev;
        const released = { ...prev };
        delete released[key];
        return released;
      });

      try {
        await apiClient(API_ENDPOINTS.competitions.goldenBallPath(competitionId, match.stage), {
          method: 'PUT',
          data: { competitionMatchId: next, round: slot.round },
        });
      } catch {
        // No snackbar on refusal (AC-16.4): the revert IS the report — the tapped card
        // un-fills and its siblings become tappable again, which says more about what to do
        // next than a floating message would.
        //
        // Drop the whole slot entry, not just this match: the siblings were dimmed off the
        // same entry, so leaving it would keep them dim for a boost that was never placed.
        releaseSlot();
        return;
      }

      // Confirmed only once the server has agreed. The fill is optimistic and appears before
      // the round trip, so it is not by itself evidence the boost stuck. Placement and
      // clearing get their own words: they are opposite outcomes of one tap (AC-16.3).
      useSnackbarStore.getState().showSnackbar(
        next != null
          ? COMPETITION_PICK_FEEDBACK_COPY.BOOST_PLACED
          : COMPETITION_PICK_FEEDBACK_COPY.BOOST_CLEARED,
        next != null ? 'success' : 'info',
      );

      // Re-read rather than trusting the local guess: the server may have locked the slot
      // between render and tap.
      await queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.matchesMe(competitionId),
      });

      // The refetch has landed, so the server is authoritative again and the overlay has
      // nothing left to bridge. Keeping it was a real bug: `holderOfSlot` checks `key in
      // localBoosts` FIRST, so the optimistic value shadowed the server for the rest of the
      // hook's lifetime. A boost the server subsequently moved or parked — the auto-default
      // net grants a default x2 on a round's last game (specs/golden-ball-auto-default) —
      // could never surface, and the round's other cards stayed dim against a stale guess.
      releaseSlot();
    },
    [apiClient, competitionId, holderOfSlot, queryClient, rules],
  );

  /** Whether this match currently holds its slot's boost. */
  const isBoosted = useCallback(
    (match: ApiMatch): boolean => {
      const key = keyForMatch(match);
      return key != null && holderOfSlot(key) === match.competitionMatchId;
    },
    [holderOfSlot, keyForMatch],
  );

  /**
   * Whether this match's round already spent its boost on a DIFFERENT match (AC-12.1).
   *
   * False for the holder itself, which stays tappable so a mis-placed boost can be cleared and
   * re-placed. "Disable everything once placed" would strand the player for the round (AC-12.2).
   */
  const isSlotTakenByOtherMatch = useCallback(
    (match: ApiMatch): boolean => {
      const key = keyForMatch(match);
      if (key == null) return false;
      const holder = holderOfSlot(key);
      return holder != null && holder !== match.competitionMatchId;
    },
    [holderOfSlot, keyForMatch],
  );

  return { toggleBoost, isBoosted, isSlotTakenByOtherMatch };
}
